import React, { useState, useMemo, useCallback } from 'react';
import {
  View,
  ScrollView,
  StyleSheet,
  RefreshControl,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import type { DesktopMainParamList } from '@/navigation/residents/desktop/DesktopNavigator';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { useResponsive } from '@/hooks/common/useResponsive';
import { useAppSelector } from '@/store';
import { useListingNavigation } from '@/hooks/residents/useListingNavigation';
import {
  useGetListingsQuery,
  useGetRecentlyViewedQuery,
  useClearRecentlyViewedMutation,
  useGetPendingFeedbackRequestsQuery,
  useCompleteFeedbackRequestMutation,
  FeedbackRequest,
} from '@/store/api';
import { MarketplaceListing, RecentlyViewedItem } from '@/types/residents/marketplaceListing';
import { PostItemModal, LeaveFeedbackModal } from '@/components/residents/marketplace';
import { useToast } from '@/contexts/admin/ToastContext';
import {
  PageHeader,
  MarketplaceBanner,
  MarketplaceSearchBar,
  MarketplaceCategoryFilter,
  MarketplaceRecentlyViewed,
  MarketplaceListingsGrid,
} from '@/components/residents/redesign';
import type { MarketplaceCategory } from '@/components/residents/redesign';
import { filterMarketplaceListings } from '@/components/residents/redesign/marketplace/marketplaceUtils';

const HP = REDESIGN_SPACING.horizontalPadding;
const MAX_CONTENT_WIDTH = 1200;
const DESKTOP_THUMBNAIL_SIZE = { width: 160, height: 140 };

export const DesktopMarketplaceView: React.FC = () => {
  const { colors } = useThemeColors();
  const navigation = useNavigation<StackNavigationProp<DesktopMainParamList>>();
  const { isXLarge, isXXLarge } = useResponsive();
  const user = useAppSelector((state) => state.auth.user);
  const propertyId = user?.propertyId;
  const propertyName = user?.property?.marketingName || user?.property?.legalName || '';
  const { navigateToListing } = useListingNavigation();
  const { showSuccess, showError } = useToast();

  const [isPostItemModalVisible, setIsPostItemModalVisible] = useState(false);
  const [isFeedbackModalVisible, setIsFeedbackModalVisible] = useState(false);
  const [selectedFeedbackRequest, setSelectedFeedbackRequest] = useState<FeedbackRequest | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<MarketplaceCategory>('All');

  const {
    data: listings = [],
    isLoading: isLoadingListings,
    refetch: refetchListings,
  } = useGetListingsQuery({ propertyId });

  const {
    data: recentlyViewedListings = [],
  } = useGetRecentlyViewedQuery();

  const {
    data: pendingFeedbackRequests = [],
  } = useGetPendingFeedbackRequestsQuery();

  const [completeFeedbackRequest, { isLoading: isSubmittingFeedback }] = useCompleteFeedbackRequestMutation();
  const [clearRecentlyViewed] = useClearRecentlyViewedMutation();

  React.useEffect(() => {
    if (pendingFeedbackRequests.length > 0 && !isFeedbackModalVisible && !selectedFeedbackRequest) {
      setSelectedFeedbackRequest(pendingFeedbackRequests[0]);
      setIsFeedbackModalVisible(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingFeedbackRequests]);

  const recentlyViewed = useMemo<RecentlyViewedItem[]>(() =>
    recentlyViewedListings.map((listing) => ({
      id: `rv-${listing.id}`,
      image: listing.images?.[0]?.url || '',
      listing,
    })),
    [recentlyViewedListings],
  );

  const filteredListings = useMemo(
    () => filterMarketplaceListings(listings, selectedCategory, searchQuery),
    [listings, selectedCategory, searchQuery],
  );

  const gridColumns = useMemo(() => {
    if (isXXLarge) return 4;
    if (isXLarge) return 3;
    return 2;
  }, [isXLarge, isXXLarge]);

  const [refreshing, setRefreshing] = useState(false);
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await refetchListings();
    setRefreshing(false);
  }, [refetchListings]);

  const handlePostItem = () => setIsPostItemModalVisible(true);
  const handleClosePostItemModal = () => setIsPostItemModalVisible(false);
  const handlePostItemSuccess = () => refetchListings();
  const handleMyProfile = () => navigation.navigate('MarketplaceProfile');
  const handleListingPress = (listing: MarketplaceListing) => navigateToListing(listing);
  const handleRecentlyViewedPress = (item: RecentlyViewedItem) => navigateToListing(item.listing);
  const handleClearRecentlyViewed = () => { clearRecentlyViewed(); };
  const handleClearSearch = useCallback(() => setSearchQuery(''), []);

  const handleCloseFeedbackModal = () => {
    setIsFeedbackModalVisible(false);
    setSelectedFeedbackRequest(null);
  };

  const handleSubmitFeedback = async (rating: number, text?: string) => {
    if (!selectedFeedbackRequest) return;
    try {
      await completeFeedbackRequest({
        requestId: selectedFeedbackRequest.id,
        rating,
        text,
      }).unwrap();
      showSuccess('Your feedback has been submitted!');
      handleCloseFeedbackModal();
    } catch {
      showError('Failed to submit feedback. Please try again.');
    }
  };

  const subtitle = propertyName
    ? `${propertyName} · ${listings.length} listing${listings.length !== 1 ? 's' : ''}`
    : `${listings.length} listing${listings.length !== 1 ? 's' : ''}`;

  return (
    <>
      <ScrollView
        style={styles.container}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={handleRefresh}
            tintColor={colors.text.primary}
          />
        }
      >
        <PageHeader title="Marketplace" subtitle={subtitle} />

        <View style={styles.centeredContent}>
          <MarketplaceBanner onPostItem={handlePostItem} onMyProfile={handleMyProfile} />

          <View style={styles.toolbar}>
            <MarketplaceSearchBar
              value={searchQuery}
              onChangeText={setSearchQuery}
              onClear={handleClearSearch}
              containerStyle={styles.searchBar}
            />
            <MarketplaceCategoryFilter
              selectedCategory={selectedCategory}
              onSelectCategory={setSelectedCategory}
              inline
            />
          </View>

          <View style={[styles.divider, { backgroundColor: colors.separator }]} />

          {recentlyViewed.length > 0 && (
            <>
              <MarketplaceRecentlyViewed
                items={recentlyViewed}
                onItemPress={handleRecentlyViewedPress}
                onClear={handleClearRecentlyViewed}
                thumbnailSize={DESKTOP_THUMBNAIL_SIZE}
              />
              <View style={[styles.divider, { backgroundColor: colors.separator }]} />
            </>
          )}

          <MarketplaceListingsGrid
            listings={filteredListings}
            onListingPress={handleListingPress}
            isLoading={isLoadingListings}
            numColumns={gridColumns}
          />
        </View>
      </ScrollView>

      <PostItemModal
        visible={isPostItemModalVisible}
        onClose={handleClosePostItemModal}
        onSubmitSuccess={handlePostItemSuccess}
        propertyId={propertyId}
      />
      <LeaveFeedbackModal
        visible={isFeedbackModalVisible}
        feedbackRequest={selectedFeedbackRequest}
        onClose={handleCloseFeedbackModal}
        onSubmit={handleSubmitFeedback}
        isSubmitting={isSubmittingFeedback}
      />
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  scrollContent: {
    padding: HP,
    paddingBottom: 60,
  },
  centeredContent: {
    width: '100%',
    maxWidth: MAX_CONTENT_WIDTH,
    alignSelf: 'center',
    gap: 20,
  },
  toolbar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  searchBar: {
    flex: 1,
    maxWidth: 400,
  },
  divider: {
    height: 1,
  },
});

export default DesktopMarketplaceView;
